import {Component, OnInit} from '@angular/core';
import {League, TeamDefaultConfig} from "../../model/league";
import {UntypedFormControl, UntypedFormGroup, Validators, ReactiveFormsModule} from "@angular/forms";
import {ControlComponent} from "../../model/broadcast/control";
import {LeaguesService} from "../../services/leagues.service";
import {FileDialogComponent} from "../file-dialog/file-dialog.component";
import {take} from "rxjs/operators";
import {FileRef} from "../../model/file-ref";
import {ActivatedRoute, Router, RouterLink} from "@angular/router";
import {MatTableDataSource} from "@angular/material/table";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {MatButtonModule} from "@angular/material/button";
import {MatTabsModule} from "@angular/material/tabs";
import {MatCheckboxModule} from "@angular/material/checkbox";
import {MatSelectModule} from "@angular/material/select";
import {NgxColorsModule} from "ngx-colors";
import {NgIf, NgFor} from "@angular/common";
import {SKINS} from "../../model/broadcast/skin";

@Component({
    selector: 'app-league-dialog',
    templateUrl: './league-dialog.component.html',
    styleUrls: ['./league-dialog.component.scss'],
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatDialogModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatTabsModule,
        MatCheckboxModule,
        MatSelectModule,
        NgxColorsModule,
        RouterLink,
        NgIf,
        NgFor
    ]
})
export class LeagueDialogComponent implements OnInit {

  formData: UntypedFormGroup;
  teamsFormData: UntypedFormGroup;
  componentsFormData: UntypedFormGroup;
  partnerLogosFormData: UntypedFormGroup;
  partnerLogoUrls: string[] = [];
  teamsDataSource: MatTableDataSource<TeamDefaultConfig>;
  allComponents: ControlComponent[] = [
    'status', 'relatedGames', 'comingUp', 'batter', 'boxScore', 'homeLineup', 'awayLineup',
    'pitcher', 'homeDefense', 'awayDefense',  'comment', 'staticPartner', 'tipsportRti', 'partnerLogos',
    'intro', 'lowerThird', 'poweredBy', 'customTable'
  ];

  uid: string;
  league: League;
  readonly skins = SKINS;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public leaguesService: LeaguesService,
    public dialog: MatDialog
  ) {
    this.uid = this.route.snapshot.paramMap.get('uid');
  }

  ngOnInit(): void {
    this.formData = new UntypedFormGroup({
      // Only `url` is required: a new league is saved as a skeleton and
      // ball-score-server fills id/name/federation/teams/logos from WBSC.
      id: new UntypedFormControl(''),
      url: new UntypedFormControl('', [Validators.required]),
      name: new UntypedFormControl(''),
      federation: new UntypedFormControl(''),
      skinId: new UntypedFormControl(''),
      smallLogoUrl: new UntypedFormControl(''),
      bigLogoUrl: new UntypedFormControl(''),
      backgroundColor: new UntypedFormControl(''),
      poweredBy: new UntypedFormControl(''),
      licenced: new UntypedFormControl(false),
      archived: new UntypedFormControl(false),
    });
    if (this.uid) {
      this.leaguesService.getLeague(this.uid).pipe(take(1)).subscribe(league => {
        this.league = league;
        this.formData.patchValue(this.league);
        this.formData.get('id').disable();
        this.prepareTeamsFormData();
        this.preparePartnerLogosFormData();
        this.componentsFormData = new UntypedFormGroup({});
        this.allComponents.forEach(c => {
          this.componentsFormData.addControl(c, new UntypedFormControl(this.league.availableComponents.find(ac => ac == c) != null))
        });
        this.teamsDataSource = new MatTableDataSource<TeamDefaultConfig>(this.league.teams);
      });
    } else {
      this.league = {availableComponents: ['status'], federation: "", id: "", name: "", teams: [], url: ""};
      this.prepareTeamsFormData();
      this.preparePartnerLogosFormData();
      this.componentsFormData = new UntypedFormGroup({});
      this.allComponents.forEach(c => {
        this.componentsFormData.addControl(c, new UntypedFormControl(this.league.availableComponents.find(ac => ac == c) != null))
      });
    }
  }

  prepareTeamsFormData() {
    this.teamsFormData = new UntypedFormGroup({});
    let i: number = 0;
    this.league.teams.forEach(team => {
      this.teamsFormData.addControl('team' + i, new UntypedFormGroup({
        id: new UntypedFormControl(team.id, [Validators.required]),
        fullName: new UntypedFormControl(team.fullName, [Validators.required]),
        shortName: new UntypedFormControl(team.shortName, [Validators.required]),
        primaryColor: new UntypedFormControl(team.primaryColor, [Validators.required]),
        secondaryColor: new UntypedFormControl(team.secondaryColor),
        logoUrl: new UntypedFormControl(team.logoUrl),
        licenced: new UntypedFormControl(team.licenced),
      }));
      i++;
    });
  }

  preparePartnerLogosFormData() {
    this.partnerLogosFormData = new UntypedFormGroup({});
    this.partnerLogoUrls = [];

    // Initialize from existing partner logo URLs if available
    if (this.league.partnerLogosUrls && this.league.partnerLogosUrls.length > 0) {
      this.partnerLogoUrls = [...this.league.partnerLogosUrls];
    }

    // Create form controls for each logo URL
    for (let i = 0; i < this.partnerLogoUrls.length; i++) {
      this.partnerLogosFormData.addControl('logo' + i, new UntypedFormControl(this.partnerLogoUrls[i]));
    }

    // If no logos exist, add an empty one
    if (this.partnerLogoUrls.length === 0) {
      this.addPartnerLogo();
    }
  }

  addTeam(): void {
    this.league.teams.push({fullName: "", id: "", logoUrl: "", primaryColor: "", shortName: "", licenced: false});
    this.prepareTeamsFormData();
  }

  deleteTeam(id: string): void {
    const i = this.league.teams.findIndex(t => t.id === id);
    this.league.teams.splice(i, 1);
    this.prepareTeamsFormData()
  }

  addPartnerLogo(): void {
    this.partnerLogoUrls.push('');
    const index = this.partnerLogoUrls.length - 1;
    this.partnerLogosFormData.addControl('logo' + index, new UntypedFormControl(''));
  }

  removePartnerLogo(index: number): void {
    this.partnerLogoUrls.splice(index, 1);
    this.preparePartnerLogosFormData();
  }

  selectPartnerLogo(index: number): void {
    this.dialog.open(FileDialogComponent, {
      data: this.partnerLogosFormData.get('logo' + index).value
    }).afterClosed().pipe(take(1)).subscribe(next => {
      const file: FileRef = next;
      if (file && file.url) {
        this.partnerLogosFormData.get('logo' + index).patchValue(file.url);
        this.partnerLogoUrls[index] = file.url;
      }
    });
  }

  save(): void {
    if (this.formData.valid && this.league.availableComponents.length) {
      const rawUrl = this.formData.getRawValue().url;
      // An existing league keeps its id (== WBSC tournament id). A new league is
      // saved under a placeholder id ('new'); ball-score-server then fetches WBSC
      // and re-keys it to leagues/{tournamentId}, filling name/teams/logos.
      this.league.id = this.uid || 'new';
      this.league.url = rawUrl;
      this.league.name = this.formData.getRawValue().name;
      this.league.federation = this.formData.getRawValue().federation;
      this.league.skinId = this.formData.getRawValue().skinId || undefined;
      this.league.smallLogoUrl = this.formData.getRawValue().smallLogoUrl;
      this.league.bigLogoUrl = this.formData.getRawValue().bigLogoUrl;
      this.league.backgroundColor = this.formData.getRawValue().backgroundColor;
      this.league.poweredBy = this.formData.getRawValue().poweredBy;
      this.league.licenced = this.formData.getRawValue().licenced;
      this.league.archived = this.formData.getRawValue().archived;

      // Save partner logo URLs
      this.league.partnerLogosUrls = this.partnerLogoUrls.filter(url => url && url.trim() !== '');
      this.league.availableComponents = [];
      this.allComponents.forEach(c => {
        if (this.componentsFormData.get(c)?.value) {
          this.league.availableComponents.push(c);
        }
      });
      let i: number = 0;
      const teamsTmp: TeamDefaultConfig[] = this.league.teams;
      this.league.teams = [];
      teamsTmp.forEach(team => {
        this.league.teams.push(this.teamsFormData.get('team'+i).value);
        i++;
      })

      this.leaguesService.addOrUpdateLeague(this.league);
      this.router.navigateByUrl('/admin');
    } else {
      console.log('invalid formdata or empty availableComponents');
    }
  }

  select(formControlName: string) {
    this.dialog.open(FileDialogComponent, {
      data: this.formData.get(formControlName).value
    }).afterClosed().pipe(take(1)).subscribe(next => {
      const file: FileRef = next;
      this.formData.get(formControlName).patchValue(file.url);
    });
  }
}
